import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useEffect } from "react";
import { Header } from "./components/layout/Header";
import { Footer } from "./components/layout/Footer";
import { Toaster } from "./components/ui/sonner";
import PWAInstallPrompt from "./components/shared/PWAInstallPrompt";
import NotFoundPage from "./pages/NotFoundPage";
import IntakeFormPage from "./pages/IntakeFormPage";
import { initAnalytics, analytics } from "./utils/analytics";

// Public Pages
import HomePage from "./pages/HomePage";
import BuildPage from "./pages/BuildPage";
import ProtectPage from "./pages/ProtectPage";
import GrowPage from "./pages/GrowPage";
import LocationsPage from "./pages/LocationsPage";
import ContactPage from "./pages/ContactPage";
import PricingPage from "./pages/PricingPage";
import PackageDetailPage from "./pages/PackageDetailPage";
import CompliancePage from "./pages/CompliancePage";
import StarterPackPage from "./pages/StarterPackPage";
import StarterPackApplyPage from "./pages/StarterPackApplyPage";
import StarterPackCheckoutPage from "./pages/StarterPackCheckoutPage";
import StarterPackSuccessPage from "./pages/StarterPackSuccessPage";
import StarterPackNotReadyPage from "./pages/StarterPackNotReadyPage";
import StarterPackExistingAuthorityPage from "./pages/StarterPackExistingAuthorityPage";
import CarrierChroniclesPage from "./pages/CarrierChroniclesPage";
import CarrierChroniclesArticlePage from "./pages/CarrierChroniclesArticlePage";
import CarrierOnboardingPage from "./pages/CarrierOnboardingPage";
import ServiceApprovalPolicyPage from "./pages/ServiceApprovalPolicyPage";
import RefundPolicyPage from "./pages/RefundPolicyPage";
import SubscriptionCancellationPolicyPage from "./pages/SubscriptionCancellationPolicyPage";
import MissionPage from "./pages/MissionPage";
import VisionPage from "./pages/VisionPage";
import CoreValuesPage from "./pages/CoreValuesPage";
import FAQPage from "./pages/FAQPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import ApplicationSuccessPage from "./pages/ApplicationSuccessPage";
import InsideTheHivePage from "./pages/InsideTheHivePage";
import QueenBAcademyPage from "./pages/QueenBAcademyPage";
import AcademyCheckoutPage from "./pages/AcademyCheckoutPage";
import ToolkitPage from "./pages/ToolkitPage";
import ToolkitCheckoutPage from "./pages/ToolkitCheckoutPage";
import ToolkitProductPage from "./pages/ToolkitProductPage";
import CertificatePreview from "./pages/CertificatePreview";
import VerifyCertificatePage from "./pages/VerifyCertificatePage";

// Business Services Division (Queen B Holdings)
import BusinessServicesHomePage from "./pages/BusinessServicesHomePage";
import BusinessIntakePage from "./pages/BusinessIntakePage";
import { BusinessServicesLayout } from "./components/layout/BusinessServicesLayout";

// Admin Pages
import AdminLoginPage from "./pages/admin/AdminLoginPage";
import AdminCommandHub from "./pages/admin/AdminCommandHub";
import AdminAcademySection from "./pages/admin/sections/AdminAcademySection";
import AdminResourcesSection from "./pages/admin/sections/AdminResourcesSection";
import AdminBuildSection from "./pages/admin/sections/AdminBuildSection";
import AdminProtectSection from "./pages/admin/sections/AdminProtectSection";
import AdminGrowSection from "./pages/admin/sections/AdminGrowSection";
import FinanceSystem from "./pages/admin/sections/FinanceSystem";
import AdminAnalyticsSection from "./pages/admin/sections/AdminAnalyticsSection";
import AdminGoogleAnalyticsSection from "./pages/admin/sections/AdminGoogleAnalyticsSection";
import AdminDocumentCenter from "./pages/admin/sections/AdminDocumentCenter";
import AdminInsightsSection from "./pages/admin/sections/AdminInsightsSection";
import AdminIntakeReviewSection from "./pages/admin/sections/AdminIntakeReviewSection";
import HiveIntelligenceSection from "./pages/admin/sections/HiveIntelligenceSection";
import AdminApplicationManagement from "./pages/admin/sections/AdminApplicationManagement";
import AdminMessagesSection from "./pages/admin/sections/AdminMessagesSection";
import AdminClientProfilesSection from "./pages/admin/sections/AdminClientProfilesSection";
import AdminDrillDownPage from "./pages/admin/AdminDrillDownPage";
import BuildCMSPage from "./pages/admin/BuildCMSPage";
import AuthCallback from "./pages/admin/AuthCallback";

// Hive Pages
import PasswordlessLoginPage, { MagicLinkVerification } from "./pages/hive/PasswordlessLoginPage";
import HiveSubscribePage from "./pages/hive/HiveSubscribePage";
import HiveSuccessPage from "./pages/hive/HiveSuccessPage";
import HiveDashboardPage from "./pages/hive/HiveDashboardPage";
import DrillDownPage from "./pages/hive/DrillDownPage";
import TaskExecutionPage from "./pages/hive/build/TaskExecutionPage";
import ToolsPage from "./pages/hive/ToolsPage";
import HiveAccessPage from "./pages/hive/HiveAccessPage";

// Analytics tracker component
const AnalyticsTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Track page view on route change
    analytics.pageView(location.pathname);
  }, [location]);
  
  return null;
};

// Scroll to top on route change
const ScrollToTop = () => {
  const location = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  
  return null;
};

// Layout wrapper for public pages
const PublicLayout = ({ children }) => (
  <>
    <Header />
    <main>{children}</main>
    <Footer />
  </>
);

// Router component that handles auth callback
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (auth callback)
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <ScrollToTop />
      <AnalyticsTracker />
      <Routes>
      {/* Public Routes - Main Lifecycle Pages */}
      <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
      <Route path="/build" element={<PublicLayout><BuildPage /></PublicLayout>} />
      <Route path="/build/apply" element={<StarterPackApplyPage />} />
      <Route path="/build/checkout" element={<StarterPackCheckoutPage />} />
      <Route path="/build/success" element={<StarterPackSuccessPage />} />
      <Route path="/build/not-ready" element={<StarterPackNotReadyPage />} />
      <Route path="/build/existing-authority" element={<StarterPackExistingAuthorityPage />} />
      <Route path="/protect" element={<PublicLayout><ProtectPage /></PublicLayout>} />
      <Route path="/grow" element={<PublicLayout><GrowPage /></PublicLayout>} />
      
      {/* Legacy Routes - Redirect to new paths */}
      <Route path="/starter-pack" element={<PublicLayout><BuildPage /></PublicLayout>} />
      <Route path="/starter-pack/apply" element={<StarterPackApplyPage />} />
      <Route path="/starter-pack/checkout" element={<StarterPackCheckoutPage />} />
      <Route path="/starter-pack/success" element={<StarterPackSuccessPage />} />
      <Route path="/starter-pack/not-ready" element={<StarterPackNotReadyPage />} />
      <Route path="/starter-pack/existing-authority" element={<StarterPackExistingAuthorityPage />} />
      
      {/* Other Public Routes */}
      <Route path="/locations" element={<PublicLayout><LocationsPage /></PublicLayout>} />
      <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
      <Route path="/pricing" element={<PublicLayout><GrowPage /></PublicLayout>} />
      <Route path="/pricing/:tier" element={<PublicLayout><PackageDetailPage /></PublicLayout>} />
      <Route path="/compliance" element={<PublicLayout><ProtectPage /></PublicLayout>} />
      <Route path="/carrier-chronicles" element={<PublicLayout><CarrierChroniclesPage /></PublicLayout>} />
      <Route path="/carrier-chronicles/:slug" element={<PublicLayout><CarrierChroniclesArticlePage /></PublicLayout>} />
      <Route path="/carrier-onboarding" element={<PublicLayout><CarrierOnboardingPage /></PublicLayout>} />
      <Route path="/intake" element={<IntakeFormPage />} />
      <Route path="/application/success" element={<PublicLayout><ApplicationSuccessPage /></PublicLayout>} />
      <Route path="/service-approval-policy" element={<PublicLayout><ServiceApprovalPolicyPage /></PublicLayout>} />
      <Route path="/refund-policy" element={<PublicLayout><RefundPolicyPage /></PublicLayout>} />
      <Route path="/subscription-cancellation-policy" element={<PublicLayout><SubscriptionCancellationPolicyPage /></PublicLayout>} />
      <Route path="/mission" element={<PublicLayout><MissionPage /></PublicLayout>} />
      <Route path="/vision" element={<PublicLayout><VisionPage /></PublicLayout>} />
      <Route path="/core-values" element={<PublicLayout><CoreValuesPage /></PublicLayout>} />
      <Route path="/faq" element={<PublicLayout><FAQPage /></PublicLayout>} />
      <Route path="/privacy-policy" element={<PublicLayout><PrivacyPolicyPage /></PublicLayout>} />
      <Route path="/terms-of-service" element={<PublicLayout><TermsOfServicePage /></PublicLayout>} />
      <Route path="/inside-the-hive" element={<PublicLayout><InsideTheHivePage /></PublicLayout>} />
      <Route path="/academy" element={<PublicLayout><QueenBAcademyPage /></PublicLayout>} />
      <Route path="/academy/checkout" element={<PublicLayout><AcademyCheckoutPage /></PublicLayout>} />
      <Route path="/toolkit" element={<PublicLayout><ToolkitPage /></PublicLayout>} />
      <Route path="/toolkit/checkout" element={<PublicLayout><ToolkitCheckoutPage /></PublicLayout>} />
      <Route path="/toolkit/:packetId" element={<PublicLayout><ToolkitProductPage /></PublicLayout>} />
      <Route path="/certificate-preview" element={<CertificatePreview />} />
      <Route path="/verify/:certificateId" element={<PublicLayout><VerifyCertificatePage /></PublicLayout>} />

      {/* Queen B Business Services Division */}
      <Route path="/business-services" element={<BusinessServicesLayout><BusinessServicesHomePage /></BusinessServicesLayout>} />
      <Route path="/business-services/intake/:slug" element={<BusinessIntakePage />} />
      <Route path="/business-services/*" element={<BusinessServicesLayout><BusinessServicesHomePage /></BusinessServicesLayout>} />
      
      {/* Admin Routes (no header/footer) */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin" element={<AdminCommandHub />} />
      <Route path="/admin/academy" element={<AdminAcademySection />} />
      <Route path="/admin/resources" element={<AdminResourcesSection />} />
      <Route path="/admin/documents" element={<AdminDocumentCenter />} />
      <Route path="/admin/insights" element={<AdminInsightsSection />} />
      <Route path="/admin/intake" element={<AdminIntakeReviewSection />} />
      <Route path="/admin/intelligence" element={<HiveIntelligenceSection />} />
      <Route path="/admin/applications" element={<AdminApplicationManagement />} />
      <Route path="/admin/messages" element={<AdminMessagesSection />} />
      <Route path="/admin/clients" element={<AdminClientProfilesSection />} />
      <Route path="/admin/build" element={<AdminBuildSection />} />
      <Route path="/admin/protect" element={<AdminProtectSection />} />
      <Route path="/admin/grow" element={<AdminGrowSection />} />
      <Route path="/admin/finance" element={<FinanceSystem />} />
      <Route path="/admin/analytics" element={<AdminAnalyticsSection />} />
      <Route path="/admin/google-analytics" element={<AdminGoogleAnalyticsSection />} />
      <Route path="/admin/drill-down/:metricType" element={<AdminDrillDownPage />} />
      <Route path="/admin/build-cms" element={<BuildCMSPage />} />
      
      {/* Hive Routes (no header/footer) */}
      <Route path="/hive" element={<HiveAccessPage />} />
      <Route path="/hive/access" element={<HiveAccessPage />} />
      <Route path="/hive/login" element={<PasswordlessLoginPage />} />
      <Route path="/hive/verify" element={<MagicLinkVerification />} />
      <Route path="/hive/subscribe" element={<HiveSubscribePage />} />
      <Route path="/hive/success" element={<HiveSuccessPage />} />
      <Route path="/hive/dashboard" element={<HiveDashboardPage />} />
      <Route path="/hive/drill-down/:metricType" element={<DrillDownPage />} />
      <Route path="/hive/build/task/:taskId" element={<TaskExecutionPage />} />
      <Route path="/hive/tools" element={<ToolsPage />} />
      {/* 404 catch-all — MUST be last */}
      <Route path="*" element={<PublicLayout><NotFoundPage /></PublicLayout>} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="min-h-screen bg-[#050505]">
        {/* Noise Overlay */}
        <div className="noise-overlay" />
        
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        
        <Toaster />
        <PWAInstallPrompt />
      </div>
    </HelmetProvider>
  );
}

export default App;
